import {useRef, useState} from "react";
import {User} from "../User/User";
import {useDispatch, useSelector} from "react-redux";
import {getCookie} from "../../hooks/getCookie";
import {API_URL} from "../../URL";
import {SET_PROFILE, SET_USER} from "../../store/constants";
import {useNavigate} from "react-router-dom";
import "./profileSettings.css";

export const ProfileSettings = ({setIsOpenProfileModal}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const user = useSelector((state) => state.user.user);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [avatarBase64, setAvatarBase64] = useState(null);

    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({x: 0, y: 0});
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({x: 0, y: 0});
    const imgRef = useRef(null);
    const [passwordData, setPasswordData] = useState({
        old_password: "",
        new_password: "",
        confirm_password: "",
    });

    const closeProfileModal = () => {
        setIsOpenProfileModal(false);
        setAvatarPreview(null);
        setAvatarBase64(null);
        setZoom(1);
        setPosition({x: 0, y: 0});
        setPasswordData({
            old_password: "",
            new_password: "",
            confirm_password: "",
        });
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = () => {
            setAvatarBase64(reader.result);
            setAvatarPreview(reader.result);
            setZoom(1);
            setPosition({x: 0, y: 0});
        };

        reader.readAsDataURL(file);
    };

    const handlePasswordChange = (e) => {
        const {name, value} = e.target;

        setPasswordData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleMouseDown = (e) => {
        if (!avatarPreview) return;

        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        });
    };

    const CONTAINER_SIZE = 260;


    const handleMouseMove = (e) => {
        if (!isDragging || !imgRef.current) return;

        const rect = imgRef.current.getBoundingClientRect();

        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        const maxX = Math.max(0, (rect.width - CONTAINER_SIZE) / 2);
        const maxY = Math.max(0, (rect.height - CONTAINER_SIZE) / 2);

        setPosition({
            x: Math.max(-maxX, Math.min(maxX, newX)),
            y: Math.max(-maxY, Math.min(maxY, newY)),
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const logout = async () => {
        try {
            const csrfToken = getCookie("csrftoken");

            const response = await fetch(API_URL + "/logout/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                credentials: "include",
            });

            await response.json();

            if (response.ok) {
                dispatch({type: SET_USER, payload: null});
                navigate("/");
            }
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const handleSaveProfile = async () => {
        const payload = {
            id: user.id,
            avatar: avatarBase64,
            avatar_crop: {
                zoom,
                x: position.x,
                y: position.y,
            },
            old_password: passwordData.old_password,
            new_password: passwordData.new_password,
            confirm_password: passwordData.confirm_password,
        };

        const response = await fetch(API_URL + "/post/edit_profile/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({payload}),
        });

        const data = await response.json();

        if (!response.ok) {
            alert(data.detail || "Error");
            return;
        }

        dispatch({
            type: SET_PROFILE,
            payload: data.user.user_settings,
        });

        closeProfileModal();
    };
    const deleteAvatar = async () => {
        const response = await fetch(API_URL + "/post/remove_avatar/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                id: user?.id
            })
        });

        const data = await response.json();

        dispatch({
            type: SET_PROFILE,
            payload: data.user.user_settings,
        });
    }
    return (
        <div className="profileModalOverlay" onMouseDown={closeProfileModal}>
            <div
                className="profileModal profileModal--crop"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="profileModal__header">
                    <h3>Profile settings</h3>

                    <button
                        type="button"
                        className="profileModal__close"
                        onClick={closeProfileModal}
                    >
                        ×
                    </button>
                </div>

                <div className="avatarCrop">
                    {avatarPreview ? (
                        <>
                            <div
                                className={`avatarCrop__container ${
                                    isDragging ? "avatarCrop__container--dragging" : ""
                                }`}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                            >
                                <img
                                    ref={imgRef}
                                    src={avatarPreview}
                                    alt="avatar"
                                    draggable={false}
                                    style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: `
            translate(-50%, -50%)
            translate(${position.x}px, ${position.y}px)
            scale(${zoom})
        `,
                                    }}
                                />

                                <div className="avatarCrop__frame"/>
                            </div>

                            <div className="avatarCrop__controls">
                                <span className="avatarCrop__icon">▣</span>

                                <input
                                    type="range"
                                    min="1"
                                    max="3"
                                    step="0.01"
                                    value={zoom}
                                    onChange={(e) => setZoom(Number(e.target.value))}
                                />

                                <span className="avatarCrop__icon avatarCrop__icon--big">
                                    ▣
                                </span>
                            </div>
                        </>
                    ) : (
                        <div className="profileModal__avatarEmpty">
                            <User user={user}/>
                        </div>
                    )}
                    <div className="profileModal__btns">
                        <label className="profileModal__uploadBtn">
                            Upload avatar
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleAvatarChange}
                                hidden
                            />
                        </label>

                        {user?.user_settings?.avatar && (
                            <button
                                type="button"
                                className="button__red"
                                onClick={deleteAvatar}
                            >
                                Remove Avatar
                            </button>
                        )}
                    </div>
                </div>

                <div className="profileModal__form">
                    <label>
                        Old password
                        <input
                            type="password"
                            name="old_password"
                            value={passwordData.old_password}
                            onChange={handlePasswordChange}
                            placeholder="Old password"
                        />
                    </label>

                    <label>
                        New password
                        <input
                            type="password"
                            name="new_password"
                            value={passwordData.new_password}
                            onChange={handlePasswordChange}
                            placeholder="New password"
                        />
                    </label>

                    <label>
                        Confirm password
                        <input
                            type="password"
                            name="confirm_password"
                            value={passwordData.confirm_password}
                            onChange={handlePasswordChange}
                            placeholder="Confirm password"
                        />
                    </label>
                </div>

                <div className="profileModal__actions">
                    <button
                        type="button"
                        className="button__red"
                        onClick={logout}
                    >
                        Log out
                    </button>

                    <button
                        type="button"
                        className="profileModal__save"
                        onClick={handleSaveProfile}
                    >
                        Save changes
                    </button>
                </div>
            </div>
        </div>
    );
};